import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import usePageTitle from '@/hooks/usePageTitle';
import useNoindex from '@/hooks/useNoindex';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { extractProductId, productSlug } from '@/utils/slugify';
import { categoryLabel, categoryUrlSlug } from '@/utils/categories';
import ProductReviews from '@/components/products/ProductReviews';
import ProductTabs from '@/components/products/ProductTabs';
import ProductPdfCard from '@/components/products/ProductPdfCard';
import RecentlyViewed, { trackProductView } from '@/components/products/RecentlyViewed';
import SparePartsList from '@/components/products/SparePartsList';
import ImageGallery from '@/components/products/ImageGallery';
import { motion } from 'framer-motion';
import { useCart } from '@/context/CartContext';
import { ShoppingCart, Check, Star, Truck, Lock, RotateCcw, Shield, Package, Tag, Layers } from 'lucide-react';

function StarRating({ rating, count }) {
  if (!rating) return null;
  return (
    <div className="flex items-center gap-2">
      <div className="flex">
        {[1,2,3,4,5].map(s => (
          <Star key={s} className={`w-4 h-4 ${s <= Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200 fill-gray-200'}`} />
        ))}
      </div>
      {count > 0 && <span className="text-sm text-gray-500">({count} avis)</span>}
    </div>
  );
}

export default function CSProductDetail() {
  const { slug } = useParams();
  const productId = extractProductId(slug);
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);
  const [qty, setQty] = useState(1);
  // Charger directement le produit par son ID — robuste quelle que soit la taille du catalogue
  const { data: product, isLoading } = useQuery({
    queryKey: ['cs-product', productId],
    queryFn: async () => {
      return await base44.entities.Product.get(productId);
    },
  });

  // Exclure de l'index les pages "Produit introuvable" (anciens IDs, produits supprimés)
  useNoindex(!isLoading && !product);

  const productTitle = product?.name || '';
  const productMeta = product
    ? `${product.name}${product.brand ? ` — ${product.brand}` : ''}${product.description ? `. ${product.description}` : ''} | Circuit-Slot.fr`.slice(0, 155)
    : '';
  usePageTitle(productTitle, productMeta, { image: product?.image_url, type: 'product' });

  // Rediriger les URLs à ID nu ou slug obsolète vers l'URL canonique (slug--id)
  useEffect(() => {
    if (!product) return;
    const canonicalSlug = productSlug(product);
    if (slug !== canonicalSlug) {
      navigate(`/product/${canonicalSlug}`, { replace: true });
    }
  }, [product, slug, navigate]);

  // Produits similaires dans la même catégorie
  const { data: related = [] } = useQuery({
    queryKey: ['cs-related', product?.category, productId],
    queryFn: () => base44.entities.Product.filter({ category: product.category }, '-created_date', 10),
    enabled: !!product,
    select: data => data.filter(p => p.id !== productId).slice(0, 4),
  });

  // Canonical dynamique + Schema.org JSON-LD
  useEffect(() => {
    if (!product) return;

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://circuit-slot.fr/product/${productSlug(product)}`);

    // JSON-LD Product
    const availability = (product.stock_quantity !== undefined && product.stock_quantity !== null)
      ? (product.stock_quantity > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock')
      : 'https://schema.org/InStock';

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: product.long_description || product.description || '',
      image: [product.image_url, product.image2_url, product.image3_url, product.image4_url, product.image5_url, product.image6_url, product.image7_url, product.image8_url, product.image9_url, product.image10_url].filter(Boolean),
      brand: product.brand ? { '@type': 'Brand', name: product.brand } : undefined,
      sku: product.reference || product.id,
      mpn: product.reference_fournisseur || product.reference || undefined,
      gtin13: product.ean || undefined,
      offers: {
        '@type': 'Offer',
        url: `https://circuit-slot.fr/product/${productSlug(product)}`,
        priceCurrency: 'EUR',
        price: product.price?.toFixed(2),
        availability,
        seller: { '@type': 'Organization', name: 'Circuit-Slot' },
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingRate: {
            '@type': 'MonetaryAmount',
            value: '5.90',
            currency: 'EUR',
          },
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: 'FR',
          },
          deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            handlingTime: {
              '@type': 'QuantitativeValue',
              minValue: 0,
              maxValue: 1,
              unitCode: 'DAY',
            },
            transitTime: {
              '@type': 'QuantitativeValue',
              minValue: 2,
              maxValue: 5,
              unitCode: 'DAY',
            },
          },
        },
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          applicableCountry: 'FR',
          returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
          merchantReturnDays: 14,
          returnMethod: 'https://schema.org/ReturnByMail',
          returnFees: 'https://schema.org/FreeReturn',
        },
      },
      ...(product.rating && product.review_count > 0 ? {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: product.rating,
          reviewCount: product.review_count,
          bestRating: 5,
          worstRating: 1,
        },
      } : {}),
    };

    let script = document.querySelector('script[data-product-jsonld]');
    if (!script) {
      script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-product-jsonld', '1');
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);

    // BreadcrumbList JSON-LD
    const breadcrumbLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://circuit-slot.fr/' },
        { '@type': 'ListItem', position: 2, name: 'Boutique', item: 'https://circuit-slot.fr/shop' },
        { '@type': 'ListItem', position: 3, name: categoryLabel(product.category), item: `https://circuit-slot.fr/shop?cat=${categoryUrlSlug(product.category)}` },
        { '@type': 'ListItem', position: 4, name: product.name, item: `https://circuit-slot.fr/product/${productSlug(product)}` },
      ],
    };
    let bcrumbScript = document.querySelector('script[data-breadcrumb-jsonld]');
    if (!bcrumbScript) {
      bcrumbScript = document.createElement('script');
      bcrumbScript.setAttribute('type', 'application/ld+json');
      bcrumbScript.setAttribute('data-breadcrumb-jsonld', '1');
      document.head.appendChild(bcrumbScript);
    }
    bcrumbScript.textContent = JSON.stringify(breadcrumbLd);

    // VideoObject JSON-LD — requis pour l'indexation vidéo Google
    let videoScript = null;
    if (product.video_url) {
      const videoLd = {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: `${product.name} — vidéo`,
        description: (product.long_description || product.description || `${product.name} en action sur circuit de slot.`).slice(0, 300),
        thumbnailUrl: [product.image_url, product.thumbnail_url, product.image2_url, product.image3_url].find(Boolean) || product.image_url,
        contentUrl: product.video_url,
        embedUrl: `https://circuit-slot.fr/product/${productSlug(product)}`,
        uploadDate: product.created_date ? new Date(product.created_date).toISOString() : new Date().toISOString(),
        publisher: { '@type': 'Organization', name: 'Circuit-Slot' },
      };
      videoScript = document.querySelector('script[data-video-jsonld]');
      if (!videoScript) {
        videoScript = document.createElement('script');
        videoScript.setAttribute('type', 'application/ld+json');
        videoScript.setAttribute('data-video-jsonld', '1');
        document.head.appendChild(videoScript);
      }
      videoScript.textContent = JSON.stringify(videoLd);
    } else {
      document.querySelector('script[data-video-jsonld]')?.remove();
    }

    // Track recently viewed
    trackProductView(product);

    return () => {
      // Restore canonical to homepage on unmount
      if (canonical) canonical.setAttribute('href', 'https://circuit-slot.fr/');
      if (script) script.remove();
      if (bcrumbScript) bcrumbScript.remove();
      if (videoScript) videoScript.remove();
    };
  }, [product]);

  const handleAdd = () => {
    if (!product) return;
    for (let i = 0; i < qty; i++) addItem(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  // Collect all images
  const images = product ? [
    product.image_url, product.image2_url, product.image3_url,
    product.image4_url, product.image5_url, product.image6_url,
    product.image7_url, product.image8_url, product.image9_url, product.image10_url
  ].filter(Boolean) : [];
  const videoUrl = product?.video_url || null;
  const videoUrl2 = product?.video_url2 || null;

  const discount = product?.compare_price
    ? Math.round((1 - product.price / product.compare_price) * 100)
    : null;

  const hasStock = product?.stock_quantity !== undefined && product?.stock_quantity !== null;
  const maxQty = hasStock ? Math.max(1, product.stock_quantity) : 99;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-cs-blue rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-xl mx-auto py-24 text-center">
        <p className="text-gray-500 mb-4">Produit introuvable.</p>
        <Link to="/shop" className="text-cs-blue hover:underline">← Retour au catalogue</Link>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100 py-2.5 px-4">
        <div className="max-w-screen-xl mx-auto text-xs text-gray-400 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-cs-blue transition-colors">Accueil</Link>
          <span>/</span>
          <Link to="/shop" className="hover:text-cs-blue transition-colors">Boutique</Link>
          <span>/</span>
          <Link to={`/shop?cat=${categoryUrlSlug(product.category)}`} className="hover:text-cs-blue transition-colors">
            {categoryLabel(product.category)}
          </Link>
          <span>/</span>
          <span className="text-gray-600 truncate max-w-xs">{product.name}</span>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 xl:gap-16">

          {/* LEFT — Image Gallery */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
            <ImageGallery images={images} videoUrl={videoUrl} videoUrl2={videoUrl2} productName={product.name} productBrand={product.brand} />
          </motion.div>

          {/* RIGHT — Product Info */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
            className="flex flex-col">

            {/* Brand + Ref */}
            <div className="flex items-center justify-between mb-2">
              {product.brand && (
                <span className="text-xs font-bold text-cs-blue uppercase tracking-widest">{product.brand}</span>
              )}
              {product.reference_fournisseur && (
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">Réf: {product.reference_fournisseur.replace(/ /g, '-')}</span>
              )}
            </div>

            <h1 className="font-display font-bold text-2xl md:text-3xl text-gray-900 leading-tight mb-3">
              {product.name}
            </h1>

            <StarRating rating={product.rating} count={product.review_count} />

            {/* Price */}
            <div className="flex items-baseline gap-3 mt-4 mb-5 pb-5 border-b border-gray-100">
              <span className="text-4xl font-bold text-gray-900">{product.price?.toFixed(2)} €</span>
              {product.compare_price && (
                <>
                  <span className="text-xl text-gray-400 line-through">{product.compare_price?.toFixed(2)} €</span>
                  <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                    -{discount}%
                  </span>
                </>
              )}
            </div>

            {/* Short description */}
            {product.description && (
              <p className="text-sm text-gray-600 leading-relaxed mb-4">{product.description}</p>
            )}

            {/* Badges */}
            <div className="flex gap-2 mb-4">
              {product.badge && (
                <span className="bg-cs-blue text-white text-xs font-bold px-3 py-1 rounded-full">{product.badge}</span>
              )}
              {product.scale && (
                <span className="bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                  <Layers className="w-3 h-3" /> Échelle {product.scale}
                </span>
              )}
              {(() => {
                const outOfStock = product.stock_quantity !== undefined && product.stock_quantity !== null && product.stock_quantity === 0;
                return (
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1 ${outOfStock ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>
                    <Package className="w-3 h-3" />
                    {outOfStock ? 'Rupture de stock' : product.stock_quantity > 0 ? `En stock (${product.stock_quantity})` : 'En stock'}
                  </span>
                );
              })()}
            </div>

            {/* Quantity + Add to cart */}
            <div className="flex items-center gap-3 mb-5">
              <div className="flex items-center border-2 border-gray-200 rounded-lg overflow-hidden">
                <button onClick={() => setQty(q => Math.max(1, q - 1))}
                  className="w-11 h-12 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors text-lg font-bold">−</button>
                <span className="w-12 h-12 flex items-center justify-center font-bold text-gray-900 text-base border-x-2 border-gray-200">{qty}</span>
                <button onClick={() => setQty(q => Math.min(maxQty, q + 1))}
                  disabled={qty >= maxQty}
                  className="w-11 h-12 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors text-lg font-bold disabled:opacity-30 disabled:cursor-not-allowed">+</button>
              </div>
              <button
                onClick={handleAdd}
                disabled={product.stock_quantity !== undefined && product.stock_quantity !== null && product.stock_quantity === 0}
                className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-lg font-display font-bold text-base transition-all duration-300 shadow-sm ${
                  added ? 'bg-green-500 text-white shadow-green-200' :
                  (product.stock_quantity !== undefined && product.stock_quantity !== null && product.stock_quantity === 0) ? 'bg-gray-200 text-gray-400 cursor-not-allowed' :
                  'bg-cs-blue text-white hover:bg-cs-blue-dark shadow-blue-200'
                }`}
              >
                {added ? <><Check className="w-5 h-5" /> AJOUTÉ AU PANIER</> :
                  (product.stock_quantity !== undefined && product.stock_quantity !== null && product.stock_quantity === 0) ? 'RUPTURE DE STOCK' :
                  <><ShoppingCart className="w-5 h-5" /> AJOUTER AU PANIER</>}
              </button>
            </div>

            {/* Specs Table */}
            {product.specs?.length > 0 && (
              <div className="mb-6">
                <h3 className="font-display font-bold text-sm text-gray-800 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-cs-blue" /> Caractéristiques techniques
                </h3>
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  {product.specs.map((s, i) => (
                    <div key={i} className={`flex justify-between items-center px-4 py-2.5 text-sm ${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                      <span className="text-gray-500 font-medium">{s.label}</span>
                      <span className="font-semibold text-gray-800 text-right ml-4">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Trust badges */}
            <div className="grid grid-cols-2 gap-3 p-4 bg-gradient-to-br from-cs-blue/5 to-cs-blue/10 rounded-xl border border-cs-blue/15">
              {[
                { icon: Truck, text: 'Livraison 48/72h', sub: 'Gratuite dès 149€' },
                { icon: Lock, text: 'Paiement sécurisé', sub: 'SSL & 3D Secure' },
                { icon: RotateCcw, text: 'Retours 14 jours', sub: 'Satisfait ou remboursé' },
                { icon: Shield, text: 'Garantie 2 ans', sub: 'Service après-vente' },
              ].map(a => (
                <div key={a.text} className="flex items-start gap-2.5">
                  <a.icon className="w-4 h-4 text-cs-blue shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-xs font-semibold text-gray-800">{a.text}</p>
                    <p className="text-[10px] text-gray-500">{a.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <ProductTabs product={product} />

        <ProductPdfCard pdfUrl={product.pdf_url} productName={product.name} />

        <SparePartsList product={product} />

        <RecentlyViewed currentProductId={productId} />

        {/* Related Products */}
        {related.length > 0 && (
          <div className="mt-16 pt-10 border-t border-gray-100">
            <h2 className="font-display font-bold text-xl text-gray-900 mb-6">PRODUITS SIMILAIRES</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {related.map(p => {
                return (
                  <Link key={p.id} to={`/product/${productSlug(p)}`}
                    className="group bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
                    <div className="aspect-square bg-gray-50 overflow-hidden">
                      <img src={p.thumbnail_url || p.image_url} alt={p.name}
                        loading="lazy" decoding="async"
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 p-2" />
                    </div>
                    <div className="p-3">
                      {p.brand && <p className="text-[10px] font-bold text-cs-blue uppercase tracking-wide mb-1">{p.brand}</p>}
                      <p className="text-sm font-medium text-gray-800 line-clamp-2 leading-tight mb-2 group-hover:text-cs-blue transition-colors">{p.name}</p>
                      <p className="font-bold text-gray-900">{p.price?.toFixed(2)} €</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}